import React, { useContext, useEffect, useState, CSSProperties } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import { Redirect } from 'react-router-dom';
import GroupAPI from '../../api/GroupAPI';
import { useSnackbar } from 'notistack';
import { IKeycloakTeam } from './TeamAccess';
import EditAccess from 'components/SuperAdmin/EditAccess';
import AdminInvites from 'components/SuperAdmin/AdminInvites';
import CreditsUsageOverview from 'pages/credit-plans/CreditsUsageOverview';
import BackButton from 'components/Common/BackButton';
import PaymentsAPI from 'api/PaymentsAPI';
import UsersAPI from 'api/UsersAPI';

const TeamSelectView: React.FC = (props: any) => {
  const { cur_user } = useContext(GlobalContext);
  const [activeTab, setActiveTab] = useState<
    'subscriptions' | 'invites' | 'usage'
  >('subscriptions');
  const [curTeam, setCurTeam] = useState<IKeycloakTeam>({
    id: '',
    name: '',
    path: '',
    subGroups: [],
    realmRoles: [],
  });
  const [stripeUser, setStripeUser] = useState({ isLoading: true, data: [] });
  const [customerInfo, setCustomerInfo] = useState<any>({
    isLoading: true,
    data: {},
  });
  const [adminUser, setAdminUser] = useState<any>({
    isLoading: true,
    data: {},
  });
  const { enqueueSnackbar } = useSnackbar();

  if (cur_user.bite_super_admin === false) {
    return <Redirect to='/' />;
  }

  useEffect(() => {
    const group_id = props.match.params.id;
    GroupAPI.getGroupById(group_id)?.then((res) => {
      if (res?.success) {
        setCurTeam(res.result);
      } else {
        const err_text = `Error loading team: ${res?.result}`;
        enqueueSnackbar(err_text, { variant: 'error' });
      }
    });
  }, []);

  const getStripeCustomer = async (teamAdmin: string) => {
    if (!teamAdmin) {
      setStripeUser({ isLoading: false, data: [] });
      return;
    }
    const stripeRes = await PaymentsAPI.getCustomerByEmail(teamAdmin);
    if (stripeRes.success) {
      setStripeUser({ isLoading: false, data: stripeRes.result.data });
    } else {
      setStripeUser({ isLoading: false, data: [] });
      enqueueSnackbar(
        `Could not look up the Stripe customer for ${teamAdmin}: ${stripeRes.result}`,
        { variant: 'error' },
      );
    }
  };

  // Stripe customers are matched to the team by name. Compare ignoring case
  // and surrounding/repeated whitespace so trivial differences don't leave the
  // team with no customer (which disables trial credits and subscription creation).
  const normalizeName = (name?: string) =>
    (name || '').trim().replace(/\s+/g, ' ').toLowerCase();

  // Every customer is created with the team's id in metadata.team_id
  // (payment.service createCustomer), so match on that first. Customers made
  // before that metadata existed fall back to the name, but never one tagged
  // with another team's id. More than one match is ambiguous — a billing
  // change could land on the wrong customer — so it is refused, not guessed.
  const getMatchedCusId = () => {
    const customers: any[] = Array.isArray(stripeUser.data)
      ? stripeUser.data
      : [];
    const byTeamId = customers.filter(
      (cus: any) => !!curTeam.id && cus?.metadata?.team_id === curTeam.id,
    );
    const matches = byTeamId.length
      ? byTeamId
      : customers.filter(
          (cus: any) =>
            !cus?.metadata?.team_id &&
            normalizeName(cus.name) === normalizeName(curTeam.name),
        );
    if (matches.length === 1) {
      setCustomerInfo({ isLoading: false, data: matches[0] });
      return;
    }
    setCustomerInfo({ isLoading: false, data: {} });
    if (matches.length > 1) {
      enqueueSnackbar(
        `${matches.length} Stripe customers match "${curTeam.name}" for the team admin's email, so subscriptions and trial credits can't be managed until the duplicates are resolved in Stripe.`,
        { variant: 'error' },
      );
    } else if (curTeam?.name) {
      enqueueSnackbar(
        `No Stripe customer named "${curTeam.name}" was found for the team admin's email, so subscriptions and trial credits can't be managed.`,
        { variant: 'warning' },
      );
    }
  };

  const getAdminUser = async () => {
    const teamAdmin = curTeam?.attributes?.team_admin?.[0];
    if (!teamAdmin) {
      return;
    }
    const res = await UsersAPI.searchUsers(teamAdmin);
    if (res?.success) {
      setAdminUser({ isLoading: false, data: res.result[0] });
    }
  };

  useEffect(() => {
    if (curTeam?.attributes?.team_admin?.[0]) {
      getStripeCustomer(curTeam?.attributes?.team_admin?.[0]);
    }
  }, [curTeam]);

  useEffect(() => {
    if (!stripeUser.isLoading) {
      getMatchedCusId();
      getAdminUser();
    }
  }, [stripeUser]);

  const styles: Record<string, CSSProperties> = {
    page: {
      width: '100%',
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      color: 'var(--text-primary-color)',
      padding: '30px',
      marginTop: '1px',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    tabsWrapper: {
      marginTop: '16px',
      marginBottom: '24px',
    },
  };

  return (
    <div style={styles.page}>
      <BackButton />
      <div className='screening-tabs-container' style={styles.tabsWrapper}>
        <div className='screening-tabs-inner'>
          <div
            className={
              activeTab === 'subscriptions'
                ? 'screening-tab-btn-selected'
                : 'screening-tab-btn'
            }
            onClick={() => setActiveTab('subscriptions')}
          >
            <div
              className={
                activeTab === 'subscriptions'
                  ? 'screening-tab-btn-text-selected'
                  : 'screening-tab-btn-text'
              }
            >
              Subscriptions
            </div>
          </div>
          <div
            className={
              activeTab === 'invites'
                ? 'screening-tab-btn-selected'
                : 'screening-tab-btn'
            }
            onClick={() => setActiveTab('invites')}
          >
            <div
              className={
                activeTab === 'invites'
                  ? 'screening-tab-btn-text-selected'
                  : 'screening-tab-btn-text'
              }
            >
              Invite Users
            </div>
          </div>
          <div
            className={
              activeTab === 'usage'
                ? 'screening-tab-btn-selected'
                : 'screening-tab-btn'
            }
            onClick={() => setActiveTab('usage')}
          >
            <div
              className={
                activeTab === 'usage'
                  ? 'screening-tab-btn-text-selected'
                  : 'screening-tab-btn-text'
              }
            >
              Usage
            </div>
          </div>
        </div>
      </div>
      {activeTab === 'subscriptions' && (
        <EditAccess
          curTeam={curTeam}
          customerInfo={customerInfo}
          adminUser={adminUser}
          onSubscriptionsChanged={() => {
            const teamAdmin = curTeam?.attributes?.team_admin?.[0];
            return teamAdmin ? getStripeCustomer(teamAdmin) : undefined;
          }}
        />
      )}
      {activeTab === 'invites' && (
        <AdminInvites curTeam={curTeam} customerInfo={customerInfo} />
      )}
      {activeTab === 'usage' && (
        <div style={{ padding: '0 24px' }}>
          <CreditsUsageOverview
            cur_user={cur_user}
            adminTeamId={curTeam.id}
            onManageCredits={() => undefined}
            canManageBilling={false}
          />
        </div>
      )}
    </div>
  );
};

export default TeamSelectView;
